import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Button,
  Grid,
  Autocomplete,
  TextField,
  SwipeableDrawer,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction
} from '@mui/material';

import {
  ExpandMore,
  Edit,
  Delete as DeleteIcon,
  Launch,
  Close,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  Healing as HealingIcon,
  SportsBasketball,
  Straighten as StraightenIcon,
  FolderOpen as FolderOpenIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AcbExperienceBadge from '../components/AcbExperienceBadge';
import TanteoBadge from '../components/TanteoBadge';
import FreeAgentBadge from '../components/FreeAgentBadge';
import ContractIcon from '../components/ContractIcon';
import PlayerTypeToggle from '../components/PlayerTypeToggle';
import { normalizeTypeToggleValue } from '../utils/playerTypes';

const getInitials = (nom, prenom) => {
  const firstInitial = nom ? nom[0] : '';
  const secondInitial = prenom ? prenom[0] : '';
  return (firstInitial + secondInitial).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return '#757575';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
};

const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#000000';
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const joinWithDash = (...items) => items.filter(Boolean).join(' - ');

const getFormIcon = (forme) => {
  if (!forme) return null;
  switch (forme.toLowerCase()) {
    case 'high':
      return <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />;
    case 'low':
      return <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />;
    case 'stable':
      return <TrendingFlat sx={{ color: '#ff9800', fontSize: 20 }} />;
    default:
      return null;
  }
};

const UserAvatar = ({ user, mediaUrl, size = 24 }) => (
  <Tooltip
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: 'white',
          '& .MuiTooltip-arrow': { color: 'white' },
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)'
        }
      }
    }}
    title={
      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          src={`${mediaUrl}/users/${user.id}.png`}
          sx={{ width: 40, height: 40 }}
        />
        <Box>
          <Typography sx={{ fontWeight: 'bold', color: 'black' }}>
            {user.prenom} {user.nom}
          </Typography>
        </Box>
      </Box>
    }
  >
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: `${size * 0.5}px`,
        bgcolor: getAvatarColor(user.nom + user.prenom)
      }}
    >
      {getInitials(user.prenom, user.nom)}
    </Avatar>
  </Tooltip>
);

const PlayerCard = ({
  player,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  disablePlayerLink
}) => {
  const { t } = useTranslation();
  // Calculate decimal age if date_naissance exists
  const calculateDecimalAge = (dateNaissance, anneeNaissance) => {
    if (!dateNaissance || !anneeNaissance) return anneeNaissance;

    const now = new Date();
    const birthDate = new Date(dateNaissance);

    // Calculate the difference in milliseconds
    const ageInMilliseconds = now - birthDate;

    // Convert to years with decimal
    const ageInYears = ageInMilliseconds / (365.25 * 24 * 60 * 60 * 1000);

    // Return formatted string
    return `${anneeNaissance} (${ageInYears.toFixed(1)})`;
  };

  const ageDisplay = calculateDecimalAge(player.date_naissance, player.annee_naissance);

  const line2 = joinWithDash(
    player.poste,
    ageDisplay,
    player.taille ? `${player.taille}cm` : null
  );

  const passportPart = player.nationalite
    ? t(`createPlayer.nationalities.${player.nationalite}`, player.nationalite) + (player.passport ? ` - (${player.passport})` : '')
    : '';

  // Build line3 with competitions_detail if available, otherwise fallback to old fields
  let line3Parts = [passportPart];

  if (player.competitions_detail && player.competitions_detail.length > 0) {
    // Group competitions by team
    const equipesMap = new Map();
    player.competitions_detail.forEach(comp => {
      if (!equipesMap.has(comp.equipe_id)) {
        equipesMap.set(comp.equipe_id, {
          equipe_nom: comp.equipe_nom,
          competitions: []
        });
      }
      equipesMap.get(comp.equipe_id).competitions.push(comp.competition_nom);
    });

    // Format as: Team1 (Comp1, Comp2) - Team2 (Comp3)
    const teamsStr = Array.from(equipesMap.values())
      .map(equipe => `${equipe.equipe_nom} (${equipe.competitions.join(', ')})`)
      .join(' - ');

    if (teamsStr) line3Parts.push(teamsStr);
  } else {
    // Fallback to old fields
    if (player.team) line3Parts.push(player.team);
    const leagues = [player.league_1, player.league_2, player.competicion_europa]
      .filter(Boolean)
      .join(' - ');
    if (leagues) line3Parts.push(leagues);
  }

  const line3 = line3Parts.filter(Boolean).join(' - ');

  return (
    <ListItem
      button
      onClick={!disablePlayerLink ? () => onPlayerClick(player) : undefined}
      sx={{
        mb: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: disablePlayerLink ? 'background.paper' : 'action.hover'
        },
        py: 2,
        cursor: disablePlayerLink ? 'default' : 'pointer'
      }}
    >
      <ListItemAvatar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography color="textSecondary" sx={{ minWidth: '25px' }}>
            {player.classement}.
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={`${mediaUrl}/photos/${player.id}.png`}
              alt={player.nom}
              sx={{ width: 64, height: 64 }}
            />
            {player.forme_indicator && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                {getFormIcon(player.forme_indicator)}
              </Box>
            )}
          </Box>
        </Box>
      </ListItemAvatar>

      <Grid container spacing={2} sx={{ ml: 1 }}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="subtitle1"
              color="text.primary"
              component="span"
              sx={{
                fontSize: '1.1rem',
                ...(disablePlayerLink
                  ? {}
                  : {
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline'
                      }
                    })
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!disablePlayerLink) onPlayerClick(player);
              }}
            >
              {player.nom}
            </Typography>

            {line2 && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: '1rem' }}
              >
                {line2}
              </Typography>
            )}

            {line3 && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: '1rem' }}
              >
                {line3}
              </Typography>
            )}
            {player.contract && (
              <Tooltip title={t('competitionsViewer.badges.contract')}>
                <Chip icon={<ContractIcon sx={{ fontSize: '0.7rem !important', color: 'white !important' }} />} label={t(`createPlayer.contractDurations.${player.contract}`, player.contract)} size="small" sx={{ height: 18, mt: 0.5, fontSize: '0.65rem', fontWeight: 'bold', backgroundColor: '#00897b', color: 'white', '& .MuiChip-icon': { ml: 0.5 } }} />
              </Tooltip>
            )}
            {/* Badges section - same style as PlayerResults */}
            {(player.acb_seasons > 0 || player.is_in_follow_list || player.is_in_informe ||
              player.is_in_intel || player.is_in_fiche_technique || player.is_in_video || player.is_in_injury || player.is_in_tanteo || player.has_medidas || !!player.is_free_agent) && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25, mt: 1 }}>
                <AcbExperienceBadge seasons={player.acb_seasons} matches={player.acb_matches} />
                {player.is_in_tanteo && (
                  <TanteoBadge teamNom={player.tanteo_nom} compact />
                )}
                {!!player.is_free_agent && (
                  <FreeAgentBadge compact />
                )}
                {player.is_in_injury && (
                  <HealingIcon sx={{ color: 'red', fontSize: 20 }} />
                )}
                {player.is_in_follow_list && (
                  <Tooltip title="Seguimiento">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#ff9800',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <VisibilityIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.is_in_informe && (
                  <Tooltip title="Informe">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <AssignmentIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.is_in_intel && (
                  <Tooltip title="INTEL">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#ff6f00',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <LightbulbIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.is_in_fiche_technique && (
                  <Tooltip title="Ficha Técnica">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#00bcd4',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <DescriptionIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.is_in_video && (
                  <Tooltip title="Video">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#9c27b0',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <VideoLibraryIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.has_medidas && (
                  <Tooltip title="Medidas">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#00bcd4',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <StraightenIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.has_documents && (
                  <Tooltip title="Documents">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#795548',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <FolderOpenIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {player.url && !disablePlayerLink && (
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            size="small"
            onClick={(e) => onPlayerUrlClick(player.url, e)}
          >
            <Launch fontSize="small" />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

const PlayerCardHorizontal = ({
  player,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  disablePlayerLink
}) => {
  const { t } = useTranslation();
  const calculateDecimalAge = (dateNaissance, anneeNaissance) => {
    if (!dateNaissance || !anneeNaissance) return anneeNaissance;
    const now = new Date();
    const birthDate = new Date(dateNaissance);
    const ageInMilliseconds = now - birthDate;
    const ageInYears = ageInMilliseconds / (365.25 * 24 * 60 * 60 * 1000);
    return `${anneeNaissance} (${ageInYears.toFixed(1)})`;
  };

  const ageDisplay = calculateDecimalAge(player.date_naissance, player.annee_naissance);
  const subline = joinWithDash(
    player.poste,
    ageDisplay,
    player.taille ? `${player.taille}cm` : null
  );

  let teamLine = '';
  if (player.competitions_detail && player.competitions_detail.length > 0) {
    const equipesMap = new Map();
    player.competitions_detail.forEach((comp) => {
      if (!equipesMap.has(comp.equipe_id)) {
        equipesMap.set(comp.equipe_id, {
          equipe_nom: comp.equipe_nom,
          competitions: []
        });
      }
      equipesMap.get(comp.equipe_id).competitions.push(comp.competition_nom);
    });
    teamLine = Array.from(equipesMap.values())
      .map((e) => e.equipe_nom)
      .join(' / ');
  } else if (player.team) {
    teamLine = player.team;
  }

  const badges = [
    player.is_in_injury && { color: 'red', icon: <HealingIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Injury' },
    player.is_in_follow_list && { color: '#ff9800', icon: <VisibilityIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Seguimiento' },
    player.is_in_informe && { color: '#4caf50', icon: <AssignmentIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Informe' },
    player.is_in_intel && { color: '#ff6f00', icon: <LightbulbIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'INTEL' },
    player.is_in_fiche_technique && { color: '#00bcd4', icon: <DescriptionIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Ficha Técnica' },
    player.is_in_video && { color: '#9c27b0', icon: <VideoLibraryIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Video' },
    player.has_medidas && { color: '#00bcd4', icon: <StraightenIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Medidas' },
    player.has_documents && { color: '#795548', icon: <FolderOpenIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Documents' }
  ].filter(Boolean);

  return (
    <Paper
      id={`player-card-h-${player.id}`}
      elevation={1}
      onClick={!disablePlayerLink ? () => onPlayerClick(player) : undefined}
      sx={{
        flex: '0 0 auto',
        width: 200,
        p: 1.25,
        scrollSnapAlign: 'start',
        position: 'relative',
        cursor: disablePlayerLink ? 'default' : 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': disablePlayerLink
          ? {}
          : {
              transform: 'translateY(-2px)',
              boxShadow: 4,
              borderColor: 'primary.light'
            }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 6,
          left: 6,
          minWidth: 26,
          height: 22,
          px: 0.75,
          borderRadius: '11px',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          zIndex: 1
        }}
      >
        {player.classement}
      </Box>

      {player.url && !disablePlayerLink && (
        <IconButton
          size="small"
          onClick={(e) => onPlayerUrlClick(player.url, e)}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 1,
            bgcolor: 'rgba(255,255,255,0.85)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
          }}
        >
          <Launch sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, position: 'relative' }}>
        <Avatar
          src={`${mediaUrl}/photos/${player.id}.png`}
          alt={player.nom}
          sx={{ width: 72, height: 72 }}
        />
        {player.forme_indicator && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -2,
              right: 'calc(50% - 38px)',
              backgroundColor: 'white',
              borderRadius: '50%',
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
            }}
          >
            {getFormIcon(player.forme_indicator)}
          </Box>
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...(disablePlayerLink
            ? {}
            : { '&:hover': { color: 'primary.main' } })
        }}
        title={player.nom}
      >
        {player.nom}
      </Typography>

      {subline && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={subline}
        >
          {subline}
        </Typography>
      )}

      {teamLine && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontStyle: 'italic'
          }}
          title={teamLine}
        >
          {teamLine}
        </Typography>
      )}

      {player.contract && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={t('competitionsViewer.badges.contract')}>
            <Chip icon={<ContractIcon sx={{ fontSize: '0.7rem !important', color: 'white !important' }} />} label={t(`createPlayer.contractDurations.${player.contract}`, player.contract)} size="small" sx={{ height: 18, mt: 0.5, fontSize: '0.65rem', fontWeight: 'bold', backgroundColor: '#00897b', color: 'white', '& .MuiChip-icon': { ml: 0.5 } }} />
          </Tooltip>
        </Box>
      )}

      {(player.acb_seasons > 0 || player.is_in_tanteo || !!player.is_free_agent) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {player.acb_seasons > 0 && (
            <AcbExperienceBadge seasons={player.acb_seasons} matches={player.acb_matches} compact />
          )}
          {player.is_in_tanteo && (
            <TanteoBadge teamNom={player.tanteo_nom} compact />
          )}
          {!!player.is_free_agent && (
            <FreeAgentBadge compact />
          )}
        </Box>
      )}

      {badges.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 0.25,
            mt: 0.75
          }}
        >
          {badges.map((b, i) => (
            <Tooltip key={i} title={b.title}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: b.color,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #fff'
                }}
              >
                {b.icon}
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Paper>
  );
};

const BoardContentHorizontal = ({
  board,
  onEdit,
  onDelete,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  disablePlayerLink
}) => {
  const sortedPlayers = [...board.players].sort(
    (a, b) => a.classement - b.classement
  );

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const CARD_WIDTH = 200;
  const GAP = 12;
  const CARDS_PER_PAGE = 3;
  const PAGE_SIZE = CARDS_PER_PAGE * (CARD_WIDTH + GAP);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const updateArrows = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [sortedPlayers.length]);

  const scrollByPage = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction * PAGE_SIZE,
      behavior: 'smooth'
    });
  };

  const arrowSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    boxShadow: 1,
    '&:hover': { bgcolor: 'action.hover' }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        mb: 2,
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          minWidth: 0
        }}
      >
        <UserAvatar user={board.user} mediaUrl={mediaUrl} size={32} />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0
          }}
          title={board.libelle}
        >
          {board.libelle}
        </Typography>
        <Chip
          size="small"
          label={sortedPlayers.length}
          sx={{
            fontWeight: 600,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            minWidth: 32
          }}
        />
        <IconButton size="small" onClick={() => onEdit(board.id)} color="primary">
          <Edit fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(board)} color="error">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ position: 'relative', width: '100%', minWidth: 0, overflow: 'hidden' }}>
        <IconButton
          size="small"
          onClick={() => scrollByPage(-1)}
          sx={{
            ...arrowSx,
            left: 8,
            visibility: canScrollLeft ? 'visible' : 'hidden'
          }}
          aria-label="scroll left"
        >
          <ChevronLeft />
        </IconButton>

        <Box
          ref={scrollRef}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            alignItems: 'stretch',
            gap: 1.5,
            pl: 2,
            pr: 8,
            py: 2,
            width: '100%',
            minWidth: 0,
            scrollPaddingRight: 64,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {sortedPlayers.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ p: 2, fontStyle: 'italic' }}
            >
              —
            </Typography>
          ) : (
            sortedPlayers.map((player) => (
              <PlayerCardHorizontal
                key={player.id}
                player={player}
                onPlayerClick={onPlayerClick}
                onPlayerUrlClick={onPlayerUrlClick}
                mediaUrl={mediaUrl}
                disablePlayerLink={disablePlayerLink}
              />
            ))
          )}
        </Box>

        <IconButton
          size="small"
          onClick={() => scrollByPage(1)}
          sx={{
            ...arrowSx,
            right: 8,
            visibility: canScrollRight ? 'visible' : 'hidden'
          }}
          aria-label="scroll right"
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </Paper>
  );
};

const BoardContent = ({
  board,
  onEdit,
  onDelete,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  disablePlayerLink
}) => (
  <Paper sx={{ p: 2, mb: 2 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UserAvatar user={board.user} mediaUrl={mediaUrl} />
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          {board.libelle}
        </Typography>
      </Box>

      <Box>
        <IconButton
          size="small"
          onClick={() => onEdit(board.id)}
          color="primary"
          sx={{ mr: 1 }}
        >
          <Edit />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(board)} color="error">
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>

    <List disablePadding>
      {board.players
        .sort((a, b) => a.classement - b.classement)
        .map((player) => (
          <PlayerCard
            key={player.id}
            player={player}
            onPlayerClick={onPlayerClick}
            onPlayerUrlClick={onPlayerUrlClick}
            mediaUrl={mediaUrl}
            disablePlayerLink={disablePlayerLink}
          />
        ))}
    </List>
  </Paper>
);

const TagAccordion = ({
  tag,
  boards,
  onEdit,
  onDelete,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  expandedTags,
  setExpandedTags,
  disablePlayerLink,
  onGeneratePDFForTag,
  onOpenPista,
  showPistaButton,
  orientation = 'vertical'
}) => {
  const { t } = useTranslation();
  const isExpanded = expandedTags.has(tag.id);

  const handleChange = () => {
    const newSet = new Set(expandedTags);
    if (newSet.has(tag.id)) {
      newSet.delete(tag.id);
    } else {
      newSet.add(tag.id);
    }
    setExpandedTags(newSet);
  };

  return (
    <Accordion
      id={`accordion-tag-${tag.id}`}
      expanded={isExpanded}
      onChange={handleChange}
      sx={{ mb: 1 }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          bgcolor: 'background.default',
          borderBottom: '1px solid', 
          borderColor: 'divider'
        }}
      >
        {(() => {
          const chipEl = (
            <Chip
              key="chip"
              label={tag.libelle}
              style={{
                backgroundColor: tag.code_couleur,
                color: getContrastColor(tag.code_couleur),
                fontSize: '1.25rem',
                height: '48px',
                borderRadius: '8px'
              }}
            />
          );
          const actionsEl = (
            <Box key="actions" sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onGeneratePDFForTag(tag, boards);
                }}
              >
                PDF
              </Button>
              {showPistaButton && (
                <Button
                  variant="contained"
                  startIcon={<SportsBasketball />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenPista(boards);
                  }}
                >
                  {t('bigBoardTable.pista')}
                </Button>
              )}
            </Box>
          );
          return (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent:
                  orientation === 'horizontal' ? 'flex-start' : 'space-between'
              }}
            >
              {chipEl}
              {actionsEl}
            </Box>
          );
        })()}
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2, minWidth: 0, overflow: 'hidden' }}>
        {orientation === 'horizontal' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
            {boards.map((board) => (
              <BoardContentHorizontal
                key={board.id}
                board={board}
                onEdit={onEdit}
                onDelete={onDelete}
                onPlayerClick={onPlayerClick}
                onPlayerUrlClick={onPlayerUrlClick}
                mediaUrl={mediaUrl}
                disablePlayerLink={disablePlayerLink}
              />
            ))}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {boards.map((board) => {
              const nbBoards = boards.length;
              let mdSize;
              if (nbBoards === 1) mdSize = 12;
              else if (nbBoards === 2) mdSize = 6;
              else mdSize = 4;

              return (
                <Grid item xs={12} md={mdSize} key={board.id}>
                  <BoardContent
                    board={board}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onPlayerClick={onPlayerClick}
                    onPlayerUrlClick={onPlayerUrlClick}
                    mediaUrl={mediaUrl}
                    disablePlayerLink={disablePlayerLink}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const FilterDrawerComponent = ({
  open,
  onClose,
  players,
  users,
  selectedPlayer,
  selectedUser,
  onPlayerChange,
  onUserChange,
  mediaUrl,
  tags,
  selectedTags,
  onTagsChange,
  userRole,
  bigBoardTypeFilter,
  setBigBoardTypeFilter
}) => {
  const { t } = useTranslation();
  const isSuperAdmin = userRole === 'super admin';

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh',
          pb: 2
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Filtros</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {isSuperAdmin && (
            <Grid item xs={12}>
              <PlayerTypeToggle
                value={normalizeTypeToggleValue(bigBoardTypeFilter)}
                onChange={(val) => setBigBoardTypeFilter(val)}
                t={t}
                tPrefix="bigBoardTable"
                showProspects={false}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={players.players || []}
              getOptionLabel={(player) => player?.nom || ''}
              value={selectedPlayer}
              onChange={onPlayerChange}
              renderInput={(params) => (
                <TextField {...params} label="Filtrar por Jugador" />
              )}
              renderOption={(props, player) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Avatar
                    src={`${mediaUrl}/photos/${player.id}.png`}
                    alt={player.nom}
                    sx={{ width: 32, height: 32 }}
                  />
                  <Typography noWrap>{player.nom}</Typography>
                </Box>
              )}
              clearOnEscape
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={users}
              getOptionLabel={(user) =>
                user ? `${user.prenom} ${user.nom}` : ''
              }
              value={selectedUser}
              onChange={onUserChange}
              renderInput={(params) => (
                <TextField {...params} label="Filtrar por Usuario" />
              )}
              renderOption={(props, user) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <UserAvatar user={user} mediaUrl={mediaUrl} size={32} />
                  <Typography noWrap>{`${user.prenom} ${user.nom}`}</Typography>
                </Box>
              )}
              clearOnEscape
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              fullWidth
              options={tags}
              getOptionLabel={(tag) => tag.libelle}
              value={selectedTags}
              onChange={onTagsChange}
              renderInput={(params) => (
                <TextField {...params} label="Filtrar por Tags" />
              )}
              renderOption={(props, tag) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Chip
                    size="small"
                    label={tag.libelle}
                    style={{
                      backgroundColor: tag.code_couleur,
                      color: getContrastColor(tag.code_couleur)
                    }}
                  />
                </Box>
              )}
              renderTags={(value, getTagProps) =>
                value.map((tag, index) => (
                  <Chip
                    key={tag.id}
                    size="small"
                    label={tag.libelle}
                    {...getTagProps({ index })}
                    style={{
                      backgroundColor: tag.code_couleur,
                      color: getContrastColor(tag.code_couleur)
                    }}
                  />
                ))
              }
              clearOnEscape
            />
          </Grid>
        </Grid>
      </Box>
    </SwipeableDrawer>
  );
};

const BigBoardComponents = {
  UserAvatar,
  PlayerCard,
  PlayerCardHorizontal,
  BoardContent,
  BoardContentHorizontal,
  TagAccordion,
  FilterDrawerComponent
};

export default BigBoardComponents;